import { PricingService } from 'common/services/pricing.service';
import { DAY } from 'common/constants/time.constants';
import { WSClient } from 'BackEnd/WebSocket';
import { PRICING_TYPES } from 'common/constants/socket.constants';
import {
  findSymbolCategory,
  findTradingSessionForTV,
} from 'common/utils/quotes.utils';
import { appConfig } from 'config';
import { TRADING_VIEW_TIME_FRAMES_TO_FXTR } from '../../constants/chart.constants';

export class TradingViewService {
  requestsPer20Sec = 0;

  emptyResponsesCount = 0;

  _socket;

  instruments = new Map();

  listeners = new Map();

  lastCandlesMap = new Map();

  selectedSymbol;

  widget;

  timeFrame = 60;

  setSymbol = () => {};

  dispose() {
    this.widget.remove();
    this._socket.destroy();
    clearInterval(this.requestCountIntervalId);
  }

  mapTimeToTimeFrame(unixTimeStamp) {
    let timeFrameInMs;
    if (this.timeFrame === '1D') {
      timeFrameInMs = DAY;
    } else {
      timeFrameInMs = Number(this.timeFrame) * 60 * 1000;
    }
    return Math.floor(unixTimeStamp / timeFrameInMs) * timeFrameInMs;
  }

  writeCandle({ bid, time }) {
    let lastCandle = this.lastCandlesMap.get(this.selectedSymbol);

    const normalizedTime = this.mapTimeToTimeFrame(time);
    const normalizedTimeOfLastCandle = this.mapTimeToTimeFrame(lastCandle.time);
    const prevCandleClose = lastCandle.close;
    if (normalizedTime > normalizedTimeOfLastCandle) {
      lastCandle = {
        close: bid,
        high: Math.max(prevCandleClose, bid),
        low: Math.min(prevCandleClose, bid),
        open: prevCandleClose,
        time: normalizedTime,
      };
    } else {
      lastCandle = {
        close: bid,
        high: Math.max(prevCandleClose, bid),
        low: Math.min(prevCandleClose, bid),
        open: lastCandle.open,
        time: normalizedTime,
      };
    }
    this.lastCandlesMap.set(this.selectedSymbol, lastCandle);
    return lastCandle;
  }

  init({ streamUrl, onFail, onSymbolChange, defaultSymbol }) {
    this.selectedSymbol = defaultSymbol;

    this.requestCountIntervalId = setInterval(() => {
      if (this.requestsPer20Sec >= 40) {
        onFail?.('Too many requests');
        this.dispose();
        return;
      }
      this.requestsPer20Sec = 0;
    }, 1000 * 20);
    this._socket = new WSClient(streamUrl, {
      name: 'Trading View',
      maxFailures: appConfig.wsRetries,
      maxReconnects: appConfig.wsFailsMax,
      silenceTimeoutSeconds: 21,
      connectingTimeoutSeconds: 1,
    });
    this._socket.init();

    this.widget = new TradingView.widget({
      autosize: true,
      container: 'trading-view-chart',
      customFormatters: {
        priceFormatterFactory: (symbolInfo) => {
          return {
            format: (price) => {
              return price.toFixed(symbolInfo.digits);
            },
          };
        },
      },
      datafeed: {
        ctx: this,
        onReady(callback) {
          this.ctx._socket.send({
            type: PRICING_TYPES.GET_INSTRUMENTS,
            callback: (ins) => {
              ins.forEach((i) => this.ctx.instruments.set(i.symbol, i));
              callback({
                supports_time: true,
                exchanges: ['RW'],
                supported_resolutions: ['1', '5', '15', '30', '60', '240', 'D'],
              });
            },
          });
        },
        getBars(symbolInfo, resolution, periodParams, onResult, onError) {
          this.ctx.requestsPer20Sec++;
          const tf = resolution
            ? TRADING_VIEW_TIME_FRAMES_TO_FXTR[resolution]
            : '1D';

          PricingService.getCandles(symbolInfo.full_name, tf, {
            from: periodParams.from * 1000,
            to: periodParams.to * 1000,
          })
            .then((c = []) => {
              c.forEach((candle) => {
                const lastCandleTime = this.ctx.lastCandlesMap.get(
                  this.ctx.selectedSymbol
                )?.time;
                if (candle.time > lastCandleTime || !lastCandleTime) {
                  this.ctx.lastCandlesMap.set(this.ctx.selectedSymbol, candle);
                }
              });
              if (c.length === 0) {
                this.ctx.emptyResponsesCount++;
                if (this.ctx.emptyResponsesCount > 10) {
                  onResult(c, {
                    noData: true,
                  });
                } else {
                  onResult(c);
                }
                return;
              }
              this.ctx.emptyResponsesCount = 0;
              onResult(c, {
                noData: false,
              });
            })
            .catch((e) => onError(e));
        },
        subscribeBars(symbolInfo, resolution, onTick, listenerGuid) {
          const { name } = symbolInfo;
          this.ctx.requestsPer20Sec++;
          this.ctx.listeners.set(listenerGuid, name);
          this.ctx._socket.onSubscribe({
            type: PRICING_TYPES.TICKS_SUBSCRIBE,
            body: { symbol: name },
            callback: (tick) => {
              if (tick.symbol !== this.ctx.selectedSymbol) return;
              if (tick.bid && tick.time) {
                const candle = this.ctx.writeCandle({
                  bid: tick.bid,
                  time: tick.time,
                });
                onTick(candle);
              }
            },
            handleType: 'Tick',
            isRepeat: false,
            nonDelete: true,
          });
        },
        unsubscribeBars(listenerGuid) {
          const symbol = this.ctx.listeners.get(listenerGuid);
          this.ctx._socket.send({
            type: PRICING_TYPES.TICKS_UNSUBSCRIBE,
            body: { symbol },
          });
        },
        resolveSymbol(symbolName, onResolve) {
          const { symbol, digits, sessions } =
            this.ctx.instruments.get(symbolName);

          Promise.resolve().then(() => {
            const type = findSymbolCategory(symbol);
            onResolve({
              description: '',
              digits,
              exchange: 'RW',
              full_name: symbol,
              has_intraday: true,
              visible_plots_set: 'ohlc',
              listed_exchange: 'RW',
              minmov: (1 / 10 ** digits) * 10,
              name: symbol,
              session:
                type === 'crypto' ? '24x7' : findTradingSessionForTV(sessions),
              timezone: 'Europe/Athens',
              type,
            });
          });
        },
        searchSymbols(userInput, exchange, symbolType, onResult) {
          const res = Array.from(this.ctx.instruments)
            .filter(([k]) => {
              return k.toLowerCase()?.includes(userInput?.toLowerCase());
            })
            .map(([k]) => ({
              symbol: k,
              full_name: k,
              description: '',
              exchange: 'RW',
              type: findSymbolCategory(k),
            }));
          Promise.resolve().then(() => onResult(res));
        },
        getServerTime(callback) {
          this.ctx.requestsPer20Sec++;
          this.ctx._socket.send({
            type: PRICING_TYPES.GET_SERVER_TIME,
            callback: (message) => {
              callback(message.time / 1000);
            },
            handleType: 'Time',
            isRepeat: false,
          });
        },
      },
      disabled_features: ['edit_buttons_in_legend', 'border_around_the_chart'],
      exchanges: '',
      interval: this.timeFrame,
      library_path: './packages/charting_library/',
      loading_screen: {
        backgroundColor: '#051932',
      },
      locale: 'en',
      symbol: defaultSymbol,
      theme: 'dark',
      time_frames: [],
    });

    this.widget.onChartReady(() => {
      const { subscribe: subscribeToSymbolChange } = this.widget
        .activeChart()
        .onSymbolChanged();
      const { subscribe: subscribeToTFChange } = this.widget
        .activeChart()
        .onIntervalChanged();

      this.setSymbol = this.widget
        .activeChart()
        .setSymbol.bind(this.widget.activeChart());

      subscribeToSymbolChange(this, ({ name }) => {
        this.lastCandlesMap.clear();
        this.selectedSymbol = name;
        onSymbolChange?.(name);
        this.emptyResponsesCount = 0;
        this.requestsPer20Sec = 0;
      });

      subscribeToTFChange(this, (tf) => {
        this.lastCandlesMap.clear();
        this.timeFrame = tf;
      });
    });
  }
}
